import { CheckOutlined } from '@ant-design/icons'
import React from 'react'
import { MalindexOmrade, SkalaAlternativ } from '../../../api/malindex/malindexApi'

/**
 * FragorMatris – kompakt, touch-vänlig fråge-matris för surfplatta.
 *
 * En rad per fråga: frågetexten till vänster, skalans alternativ som knapp-celler.
 * Prioriteringen har ett eget steg (PrioriteradeOmraden) – stjärnan satt tidigare
 * här och åt bredd från frågetexten, vilket gjorde raderna högre.
 *
 * "Ej tillämpligt" renderas som en visuellt avskild kolumn längst till höger –
 * den är inte ett steg på skalan utan ett sätt att lyfta ut området ur beräkningen.
 *
 * HÖJDEN styrs medvetet: cellerna har fast höjd och bär ingen text på breda
 * skärmar. Tidigare visade den valda cellen sin etikett, som bröts till två rader
 * och drog upp hela raden – tio gånger om. Etiketterna står i kolumnrubriken,
 * så valet räcker att visa med färg och bock. Skalkolumnerna har dessutom fast
 * bredd, så all överbliven bredd går till frågetexten och ger färre radbrytningar.
 *
 * Skalan kommer alltid ur config (0–4). Komponenten är kontrollerad och håller
 * inget eget state.
 */

export interface FragorMatrisProps {
  /** Ledtext ovanför matrisen (behovLeadIn / behovLeadInUppfoljning / nyttoLeadIn). */
  leadIn: string
  omraden: MalindexOmrade[]
  /** Skalan ur config (0–4). */
  skala: SkalaAlternativ[]
  /** "Ej tillämpligt" ur config. Utelämnas → ingen sådan kolumn. */
  ejTillampligt?: SkalaAlternativ
  /** Vilken frågetext som ska visas per rad. */
  variant: 'behov' | 'nytta'
  svar: Record<number, number | undefined>
  onValj: (omradeId: number, varde: number) => void
}

/** Fast höjd på tap-ytan: 44px är Apples riktvärde och gör raden förutsägbar. */
const cellBas =
  'flex h-11 w-full items-center justify-center select-none rounded-sm border text-sm cursor-pointer transition-colors'

/** Skalkolumnernas bredd. Fast, så att frågetexten får resten. */
const SKAL_KOLUMN = 'w-11 shrink-0 sm:w-14'
const EJ_KOLUMN = 'w-14 shrink-0 sm:w-16'

const FragorMatris: React.FC<FragorMatrisProps> = ({
  leadIn,
  omraden,
  skala,
  ejTillampligt,
  variant,
  svar,
  onValj,
}) => {
  const antalBesvarade = omraden.filter((o) => svar[o.id] !== undefined).length

  const cellKlass = (aktiv: boolean) =>
    aktiv
      ? `${cellBas} border-pejlen-500 bg-pejlen-500 text-white`
      : `${cellBas} border-gray-200 bg-white text-gray-700 hover:border-pejlen-400`

  const ejKlass = (aktiv: boolean) =>
    aktiv
      ? `${cellBas} border-gray-600 bg-gray-600 text-white`
      : `${cellBas} border-dashed border-gray-300 bg-white text-gray-400 hover:border-gray-500`

  return (
    <div className="w-full">
      {/*
        Ledtexten och skalans ord ligger på SAMMA rad. Ledtexten står i frågekolumnen,
        alltså rakt ovanför de rader som fortsätter meningen: "Hur viktigt är det för
        dig att din hemtjänst…" följt av "…gör att du känner dig tryggare?".

        Raden speglar dataradens box exakt – samma px-2 och en genomskinlig 1px-ram –
        så att både ledtext och rubriker hamnar rakt ovanför sin kolumn. Utan ramen låg
        de 9px fel.

        Under sm göms bara ordgruppen, inte hela raden. Där bär cellerna sina egna ord,
        men ledtexten måste synas oavsett bredd och tar då hela raden.
      */}
      <div
        className="mb-1 flex border border-transparent px-2"
        // Inline: klassen slog inte igenom. Ytterraden centrerar ledtexten mot
        // rubrikblocket. Inne i blocket står orden kvar på samma baslinje nedifrån –
        // cellerna sträcks till samma höjd och varje ord bottnar i sin.
        style={{ alignItems: 'center' }}
      >
        <div className="min-w-0 flex-1 pr-2 text-base font-semibold leading-snug text-gray-800">{leadIn}</div>
        <div className="hidden gap-1.5 sm:flex">
          {skala.map((alt) => (
            <div
              key={alt.varde}
              className={`${SKAL_KOLUMN} flex items-end justify-center text-center text-xs font-medium leading-tight text-gray-500`}
            >
              {alt.etikett}
            </div>
          ))}
          {ejTillampligt && (
            <div
              className={`${EJ_KOLUMN} flex items-end justify-center text-center text-xs font-medium leading-tight text-gray-400`}
            >
              {ejTillampligt.etikett}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        {omraden.map((o) => {
          const valt = svar[o.id]
          const radtext = variant === 'nytta' ? o.nyttofraga : o.fraga

          return (
            <div
              key={o.id}
              className="flex flex-col gap-1.5 rounded-lg border border-gray-100 bg-gray-50 px-2 py-1.5 sm:flex-row sm:items-center"
            >
              {/* Bara frågetexten. Områdesnamnet tog plats utan att tillföra något i
                  samtalet – det lever kvar i aria-etiketterna och i grafernas axlar. */}
              <div className="min-w-0 flex-1 pr-2 text-base leading-snug text-gray-800">{radtext}</div>

              <div className="flex gap-1.5">
                {skala.map((alt) => {
                  const aktiv = valt === alt.varde
                  return (
                    <div key={alt.varde} className={SKAL_KOLUMN}>
                      <button
                        type="button"
                        aria-pressed={aktiv}
                        aria-label={`${o.namn}: ${alt.etikett}`}
                        onClick={() => onValj(o.id, alt.varde)}
                        className={cellKlass(aktiv)}
                      >
                        {/* På smala skärmar finns ingen kolumnrubrik – då bär cellen ordet. */}
                        <span className="text-xs leading-tight sm:hidden">{alt.etikett}</span>
                        <span className="hidden sm:inline">{aktiv ? <CheckOutlined /> : ''}</span>
                      </button>
                    </div>
                  )
                })}

                {ejTillampligt && (
                  <div className={EJ_KOLUMN}>
                    <button
                      type="button"
                      aria-pressed={valt === ejTillampligt.varde}
                      aria-label={`${o.namn}: ${ejTillampligt.etikett}`}
                      onClick={() => onValj(o.id, ejTillampligt.varde)}
                      className={ejKlass(valt === ejTillampligt.varde)}
                    >
                      <span className="text-xs leading-tight sm:hidden">{ejTillampligt.etikett}</span>
                      <span className="hidden sm:inline">
                        {valt === ejTillampligt.varde ? <CheckOutlined /> : ''}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/*
        Räknaren hör till matrisen och inte till steget – låg den hos anroparen
        formulerades den olika på var och en av de tre ställen matrisen används.
        Alla områden måste besvaras för att steget ska kunna lämnas.
      */}
      <p className="mt-3 text-sm text-gray-500">
        {antalBesvarade} av {omraden.length} områden besvarade
      </p>
    </div>
  )
}

export default FragorMatris
